import { DEFAULT_PROJECT_ID } from '@/constants';
import { projectReadonlySchema } from '@/models/projectReadonlySchema';
import { projectSchema } from '@/models/projectSchema';
import { userSchema } from '@/models/userSchema';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import {
  MapModel,
  OverviewImageView,
  Project,
  ProjectReadonly,
  Annotation,
  User,
} from '@/types/models';
import { ThunkConfig } from '@/types/store';
import { getError } from '@/utils/error-report/getError';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'axios';
import { apiPath } from '../apiPath';
import { PROJECT_FETCHING_ERROR_PREFIX } from './project.constants';
import { SetProjectIdParams } from './project.types';

const DEFAULT_VALUE = 0;

export const getProjectById = createAsyncThunk<Project | undefined, string, ThunkConfig>(
  'project/getProjectById',
  async id => {
    try {
      const response = await axiosInstanceNewAPI.get<Project>(apiPath.getProjectById(id));

      const isDataValid = validateDataUsingZodSchema(response.data, projectSchema);

      return isDataValid ? response.data : undefined;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PROJECT_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const setProjectId = createAsyncThunk<string, SetProjectIdParams, ThunkConfig>(
  'project/setProjectId',
  async ({ queryData }) => {
    return queryData?.id || DEFAULT_PROJECT_ID;
  },
);

export interface UpdateProjectData {
  name: string;
  version?: string;
  notifyEmail?: string;
  sbgnFormat: boolean;
  hideElementsOnZoomInFrontend: boolean;
  customLicenseName?: string;
  customLicenseUrl?: string;
  networkViewAsDefault?: boolean;
  projectId: string;
}

export interface UpdateMiriamDataDTO {
  resource: string;
  type: 'MESH_2012' | 'TAXONOMY';
}

export const moveProject = createAsyncThunk<
  void,
  { projectId: string; projectData: UpdateProjectData },
  ThunkConfig
>('project/moveProject', async ({ projectId, projectData }) => {
  const payload = { projectId: projectData.projectId };
  const response = await axiosInstanceNewAPI.post(apiPath.moveProject(projectId), payload, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (response.status !== HttpStatusCode.Ok) {
    throw new Error('Failed to move project');
  }
});

export const updateProject = createAsyncThunk<
  Project,
  { projectId: string; projectData: UpdateProjectData },
  ThunkConfig
>('project/updateProject', async ({ projectId, projectData }, { rejectWithValue }) => {
  try {
    const payload = {
      projectId,
      name: projectData.name,
      version: projectData.version || '',
      notifyEmail: projectData.notifyEmail || '',
      sbgnFormat: projectData.sbgnFormat,
      networkViewAsDefault: projectData.networkViewAsDefault ?? false,
      hideElementsOnZoomInFrontend: projectData.hideElementsOnZoomInFrontend ?? true,
      customLicenseName: projectData.customLicenseName ?? '',
      customLicenseUrl: projectData.customLicenseUrl ?? '',
    };

    const response = await axiosInstanceNewAPI.put(apiPath.updateProject(projectId), payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const parsedId = parseInt(projectId, 10);
    const safeId = Number.isNaN(parsedId) ? DEFAULT_VALUE : parsedId;
    let safeOverviewImageViews: OverviewImageView[] = [];
    if (Array.isArray(response.data.overviewImageViews)) {
      safeOverviewImageViews = response.data.overviewImageViews.filter(
        (view: unknown) => view && typeof (view as { id: number }).id === 'number',
      ) as OverviewImageView[];
    }

    const safeTopOverviewImage =
      response.data.topOverviewImage && typeof response.data.topOverviewImage.id === 'number'
        ? response.data.topOverviewImage
        : null;

    const transformedData: Project = {
      id: response.data.idObject || safeId,
      name: response.data.name || '',
      version: response.data.version || '',
      notifyEmail: response.data.notifyEmail || '',
      disease: response.data.disease || null,
      diseaseName: response.data.disease?.resource || '',
      organism: response.data.organism || null,
      organismName: response.data.organism?.resource || '',
      owner: response.data.owner || { id: 0 },
      topMap: response.data.topMap || { id: DEFAULT_VALUE, name: '' },
      sbgnFormat: response.data.sbgnFormat !== undefined ? response.data.sbgnFormat : false,
      status: response.data.status || '',
      directory: response.data.directory || '',
      progress: response.data.progress || DEFAULT_VALUE,
      logEntries: response.data.logEntries !== undefined ? response.data.logEntries : false,
      sharedInMinervaNet:
        response.data.sharedInMinervaNet !== undefined ? response.data.sharedInMinervaNet : false,
      projectId: response.data.projectId || `${projectId}`,
      creationDate: response.data.creationDate || new Date().toISOString(),
      overviewImageViews: safeOverviewImageViews,
      topOverviewImage: safeTopOverviewImage,
      license: response.data.license || null,
      customLicenseName: response.data.customLicenseName || null,
      customLicenseUrl: response.data.customLicenseUrl || null,
      networkViewAsDefault:
        response.data.networkViewAsDefault !== undefined
          ? response.data.networkViewAsDefault
          : false,
      hideElementsOnZoomInFrontend:
        response.data.hideElementsOnZoomInFrontend !== undefined
          ? response.data.hideElementsOnZoomInFrontend
          : false,
      readonly: response.data.readonly !== undefined ? response.data.readonly : false,
      sourceGitRepository: response.data.sourceGitRepository,
      sourceGitBranch: response.data.sourceGitBranch,
    };

    const isDataValid = validateDataUsingZodSchema(transformedData, projectSchema.partial());
    if (!isDataValid) {
      throw new Error('Invalid project data received from server');
    }

    return transformedData;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: { data?: unknown; status?: number; headers?: unknown };
      };
      const errorData = axiosError.response?.data as { message?: string };
      return rejectWithValue(errorData?.message || 'Failed to update project');
    }
    if (error && typeof error === 'object' && 'request' in error) {
      return rejectWithValue('No response received from server');
    }
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to update project');
  }
});

export const setProjectReadonly = createAsyncThunk<
  ProjectReadonly,
  { projectId: string; readonly: boolean },
  ThunkConfig
>('project/setProjectReadonly', async ({ projectId, readonly }, { rejectWithValue }) => {
  try {
    const response = await axiosInstanceNewAPI.put(
      apiPath.setProjectReadonly(projectId),
      { readonly },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    const isDataValid = validateDataUsingZodSchema(response.data, projectReadonlySchema);
    if (!isDataValid) {
      throw new Error('Invalid readonly data received from server');
    }

    return response.data;
  } catch (error) {
    return rejectWithValue('Failed to update project readonly status');
  }
});

export const setProjectTopMap = createAsyncThunk<
  MapModel,
  { projectId: string; mapId: number; eTag?: string },
  ThunkConfig
>('project/setProjectTopMap', async ({ projectId, mapId, eTag }, { rejectWithValue }) => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (eTag) {
      headers['If-Match'] = eTag;
    }

    const response = await axiosInstanceNewAPI.put<MapModel>(
      apiPath.setProjectTopMap(projectId),
      { id: mapId },
      { headers },
    );

    return response.data;
  } catch (error) {
    return rejectWithValue('Failed to update project top map');
  }
});

export const getProjectOwner = createAsyncThunk<User, string, ThunkConfig>(
  'project/getProjectOwner',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceNewAPI.get<User>(apiPath.getProjectOwner(projectId));

      const isDataValid = validateDataUsingZodSchema(response.data, userSchema);
      if (!isDataValid) {
        throw new Error('Invalid user data received from server');
      }

      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch project owner');
    }
  },
);

export const setProjectOwner = createAsyncThunk<
  User,
  { projectId: string; userId: number; eTag?: string },
  ThunkConfig
>('project/setProjectOwner', async ({ projectId, userId, eTag }, { rejectWithValue }) => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (eTag) {
      headers['If-Match'] = eTag;
    }

    const response = await axiosInstanceNewAPI.put<User>(
      apiPath.setProjectOwner(projectId),
      { id: userId },
      { headers },
    );

    return response.data;
  } catch (error) {
    return rejectWithValue('Failed to update project owner');
  }
});

function handleDiseaseError(error: unknown, rejectWithValue: (value: string) => unknown): unknown {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: { data?: unknown; status?: number; headers?: unknown };
    };
    const errorData = axiosError.response?.data as { message?: string };
    return rejectWithValue(errorData?.message || 'Failed to update project disease');
  }
  if (error && typeof error === 'object' && 'request' in error) {
    return rejectWithValue('No response received from server');
  }
  return rejectWithValue(
    error instanceof Error ? error.message : 'Failed to update project disease',
  );
}

export const updateProjectDisease = createAsyncThunk<
  Annotation,
  { disease: string; projectId: string },
  ThunkConfig
>('project/updateProjectDisease', async ({ projectId, disease }, { rejectWithValue }) => {
  try {
    const payload: UpdateMiriamDataDTO = {
      type: 'MESH_2012',
      resource: disease,
    };

    const response = await axiosInstanceNewAPI.put<Annotation>(
      apiPath.updateProjectDisease(projectId),
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    return response.data;
  } catch (error: unknown) {
    return handleDiseaseError(error, rejectWithValue) as ReturnType<typeof rejectWithValue>;
  }
});
export const removeProjectDisease = createAsyncThunk<
  Annotation,
  { projectId: string },
  ThunkConfig
>('project/removeProjectDisease', async ({ projectId }, { rejectWithValue }) => {
  try {
    const response = await axiosInstanceNewAPI.delete(apiPath.removeProjectDisease(projectId), {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: unknown) {
    return handleDiseaseError(error, rejectWithValue) as ReturnType<typeof rejectWithValue>;
  }
});

function handleOrganismError(error: unknown, rejectWithValue: (value: string) => unknown): unknown {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: { data?: unknown; status?: number; headers?: unknown };
    };
    const errorData = axiosError.response?.data as { message?: string };
    return rejectWithValue(errorData?.message || 'Failed to update project organism');
  }
  if (error && typeof error === 'object' && 'request' in error) {
    return rejectWithValue('No response received from server');
  }
  return rejectWithValue(
    error instanceof Error ? error.message : 'Failed to update project organism',
  );
}

export const updateProjectOrganism = createAsyncThunk<
  Annotation,
  { organism: string; projectId: string },
  ThunkConfig
>('project/updateProjectOrganism', async ({ projectId, organism }, { rejectWithValue }) => {
  try {
    const payload: UpdateMiriamDataDTO = {
      type: 'TAXONOMY',
      resource: organism,
    };
    const response = await axiosInstanceNewAPI.put<Annotation>(
      apiPath.updateProjectOrganism(projectId),
      payload,
      { headers: { 'Content-Type': 'application/json' } },
    );
    return response.data;
  } catch (error: unknown) {
    return handleOrganismError(error, rejectWithValue) as ReturnType<typeof rejectWithValue>;
  }
});

export const removeProjectOrganism = createAsyncThunk<
  Annotation,
  { projectId: string },
  ThunkConfig
>('project/removeProjectOrganism', async ({ projectId }, { rejectWithValue }) => {
  try {
    const response = await axiosInstanceNewAPI.delete(apiPath.removeProjectOrganism(projectId), {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error: unknown) {
    return handleOrganismError(error, rejectWithValue) as ReturnType<typeof rejectWithValue>;
  }
});
